import type { AxiosInstance } from "axios";

/** When `slug` is this value, `key_location_note` is required (ADM rules). */
export const KEY_LOCATION_OTHERS_SLUG = "outros";

/** When `slug` is this value, `key_location_password` is required (ADM rules). */
export const KEY_LOCATION_DIGITAL_SLUG = "chave-digital";

export function normalizeKeyLocationToken(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isOthersKeyLocationItem(item: {
  slug?: string;
  name?: string;
}): boolean {
  const slug = normalizeKeyLocationToken(item.slug ?? "");
  const name = normalizeKeyLocationToken(item.name ?? "");
  return slug === KEY_LOCATION_OTHERS_SLUG || name === "outros";
}

export function isDigitalKeyLocationItem(item: {
  slug?: string;
  name?: string;
}): boolean {
  const slug = normalizeKeyLocationToken(item.slug ?? "");
  const name = normalizeKeyLocationToken(item.name ?? "");
  const compactSlug = slug.replace(/\s+/g, "");
  return (
    slug === "chave digital" ||
    compactSlug === "chavedigital" ||
    name === "chave digital"
  );
}

export type KeyLocationCatalogItem = {
  id: number;
  name: string;
  slug: string;
};

/**
 * Fallback ID for "Outros" when the catalog failed to load.
 * Synced from the last successful catalog fetch (not a hardcoded env ID).
 */
export let KEY_LOCATION_OTHERS_ID: number | null = null;

/**
 * Fallback ID for "Chave Digital" when the catalog failed to load.
 * Synced from the last successful catalog fetch (not a hardcoded env ID).
 */
export let KEY_LOCATION_DIGITAL_ID: number | null = null;

/**
 * Label → id map for legacy string `key_location` values.
 * Synced from catalog fetches; used when catalog match is unavailable.
 */
export const KEY_LOCATION_LEGACY_MAP: Record<string, number> = {};

/** Text for display when API sends string or `{ name: string }`. */
export const normalizeKeyLocationLabel = (raw: unknown): string => {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object" && raw !== null && "name" in raw) {
    const n = (raw as { name?: unknown }).name;
    if (typeof n === "string") return n.trim();
    if (n != null) return String(n).trim();
    return "";
  }
  return String(raw).trim();
};

/** Fixed labels kept for legacy references until catalog-only UI is unified. */
export const KEY_LOCATION_OPTIONS = [
  "Portaria",
  "Proprietário",
  "7Cantos",
  "Consultor",
  "Inquilino",
] as const;

function readKeyLocationName(row: {
  name?: unknown;
  label?: unknown;
  title?: unknown;
  display_name?: unknown;
  slug?: unknown;
}): string {
  const candidates = [row.name, row.label, row.title, row.display_name];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate.trim();
    }
  }
  if (isDigitalKeyLocationItem({ slug: String(row.slug ?? "") })) {
    return "Chave Digital";
  }
  return "";
}

export function mapKeyLocationRows(
  list: Array<{
    id?: unknown;
    name?: unknown;
    label?: unknown;
    title?: unknown;
    display_name?: unknown;
    slug?: unknown;
  }>
): KeyLocationCatalogItem[] {
  return list
    .map((x) => ({
      id: Number(x.id),
      name: readKeyLocationName(x),
      slug: String(x.slug ?? "").toLowerCase(),
    }))
    .filter((x) => Number.isFinite(x.id) && x.id > 0 && x.name !== "");
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function extractKeyLocationList(res: unknown): unknown[] {
  if (Array.isArray(res)) return res;
  const body = asObject(res);
  if (!body) return [];
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.key_locations)) return body.key_locations;
  const nested = asObject(body.data);
  if (!nested) return [];
  if (Array.isArray(nested.data)) return nested.data;
  if (Array.isArray(nested.key_locations)) return nested.key_locations;
  return [];
}

export function parseKeyLocationsResponse(res: unknown): KeyLocationCatalogItem[] {
  return mapKeyLocationRows(
    extractKeyLocationList(res) as Array<{
      id?: unknown;
      name?: unknown;
      slug?: unknown;
    }>
  );
}

/** Syncs legacy map and Outros fallback ID from a live catalog response. */
export function rememberKeyLocationCatalog(
  catalog: KeyLocationCatalogItem[]
): void {
  for (const key of Object.keys(KEY_LOCATION_LEGACY_MAP)) {
    delete KEY_LOCATION_LEGACY_MAP[key];
  }
  for (const item of catalog) {
    KEY_LOCATION_LEGACY_MAP[item.name] = item.id;
  }
  const others = catalog.find((c) => isOthersKeyLocationItem(c));
  if (others) {
    KEY_LOCATION_OTHERS_ID = others.id;
  }
  const digital = catalog.find((c) => isDigitalKeyLocationItem(c));
  if (digital) {
    KEY_LOCATION_DIGITAL_ID = digital.id;
  }
}

function lookupLegacyKeyLocationId(label: string): number | null {
  const direct = KEY_LOCATION_LEGACY_MAP[label];
  if (direct != null) return direct;
  const entry = Object.entries(KEY_LOCATION_LEGACY_MAP).find(
    ([name]) => name.toLowerCase() === label.toLowerCase()
  );
  return entry != null ? entry[1] : null;
}

export type PropertyKeyLocationFields = {
  key_location?: unknown;
  key_location_id?: number | null;
};

/**
 * Resolves the select value from API property payload (id field, nested object, or legacy string).
 */
export function resolveKeyLocationId(
  property: PropertyKeyLocationFields,
  catalog: KeyLocationCatalogItem[]
): number | null {
  const rawKl = property.key_location;

  if (rawKl && typeof rawKl === "object" && rawKl !== null && "id" in rawKl) {
    const id = Number((rawKl as { id: unknown }).id);
    if (Number.isFinite(id) && id > 0) return id;
  }

  if (property.key_location_id != null) {
    const id = Number(property.key_location_id);
    if (Number.isFinite(id) && id > 0) return id;
  }

  if (typeof rawKl === "string") {
    const label = rawKl.trim();
    if (label === "") return null;

    const fromCatalog = catalog.find(
      (c) => c.name.toLowerCase() === label.toLowerCase()
    );
    if (fromCatalog) return fromCatalog.id;

    return lookupLegacyKeyLocationId(label);
  }

  return null;
}

function uniqueKeyLocations(
  items: KeyLocationCatalogItem[]
): KeyLocationCatalogItem[] {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function readLastPage(res: unknown): number {
  const body = asObject(res);
  const nested = asObject(body?.data);
  const meta = asObject(body?.meta) ?? asObject(nested?.meta);
  const lastPage = Number(meta?.last_page ?? nested?.last_page ?? 1);
  return Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1;
}

export async function fetchKeyLocations(
  client: AxiosInstance
): Promise<KeyLocationCatalogItem[]> {
  const collected: KeyLocationCatalogItem[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const res = await client.get("/api/adm/key-locations", {
      params: { page, per_page: 100 },
    });
    collected.push(...parseKeyLocationsResponse(res.data));
    lastPage = readLastPage(res.data);
    page += 1;
  } while (page <= lastPage && page <= 10);

  const catalog = uniqueKeyLocations(collected);
  rememberKeyLocationCatalog(catalog);
  return catalog;
}

export type PatchPropertyKeyLocationBody = {
  key_location_id: number | null;
  key_location_note?: string | null;
  key_location_password?: string;
};

export function patchPropertyKeyLocation(
  client: AxiosInstance,
  propertyId: number,
  body: PatchPropertyKeyLocationBody
) {
  return client.patch(`/api/adm/properties/${propertyId}/key-location`, body);
}
